import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  // ✅ Use environment-based URL
  private apiUrl = `${environment.apiUrl}/api/user`;
  private authApiUrl = `${environment.apiUrl}/api/auth`;
  private readonly TOKEN_KEY = 'access_token'; // ✅ Standard key for JWT
  private readonly ROLES_KEY = 'roles';

  constructor(private http: HttpClient) {
    console.log('🔐 AuthService initialized');
  }

  /**
   * Login with username and password
   * Returns token and roles
   *
   * ✅ NO withCredentials - JWT in Authorization header is enough
   */
  logIn(username: string, password: string) {
    const body = { username, password };
    console.log('🔓 Attempting login for user:', username);

    return this.http.post<any>(
      `${this.apiUrl}/login`,
      body
      // NO withCredentials!
    );
  }

  /**
   * Save token to localStorage
   * ✅ Called after successful login
   */
  saveToken(token: string): void {
    if (!token || token === 'null' || token === '') {
      console.error('❌ Invalid token received');
      return;
    }
    localStorage.setItem(this.TOKEN_KEY, token);
    console.log('✅ Token saved successfully');
  }

  /**
   * Save roles to localStorage
   * ✅ Called after successful login
   */
  saveRoles(roles: string[]): void {
    if (Array.isArray(roles) && roles.length > 0) {
      localStorage.setItem(this.ROLES_KEY, JSON.stringify(roles));
      console.log('✅ Roles saved:', roles);
    }
  }

  /**
   * Get user data (requires valid JWT token)
   */
  getUserData() {
    return this.http.get<any>(
      `${this.apiUrl}/userdata`
      // NO withCredentials!
    );
  }

  /**
   * Get roles from localStorage
   */
  getRoles(): string[] {
    const stored = localStorage.getItem(this.ROLES_KEY);
    return stored ? JSON.parse(stored) : [];
  }

  /**
   * Check if user has a specific role
   */
  hasRole(role: string): boolean {
    return this.getRoles().includes(role);
  }

  /**
   * Check if user is admin
   */
  isAdmin(): boolean {
    return this.hasRole('Admin');
  }

  /**
   * Check if user is regular user
   */
  isUser(): boolean {
    return this.hasRole('User');
  }

  /**
   * Check if user is logged in
   */
  isLoggedIn(): boolean {
    const token = this.getToken();
    return !!token && token !== 'null' && token !== '';
  }

  /**
   * Get stored token
   * ✅ Returns null if no valid token exists
   */
  getToken(): string | null {
    const token = localStorage.getItem(this.TOKEN_KEY);
    if (!token || token === 'null' || token === '') {
      return null;
    }
    return token;
  }

  /**
   * Logout user
   */
  logout() {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.ROLES_KEY);
    console.log('🚪 User logged out');
  }

  /**
   * Request password reset email
   */
  forgotPassword(email: string) {
    return this.http.post<any>(`${this.authApiUrl}/forgot-password`, { email });
  }

  /**
   * Validate a password reset token
   */
  verifyResetToken(token: string) {
    return this.http.get<any>(`${this.authApiUrl}/verify-email/${token}`);
  }

  /**
   * Reset password with token
   */
  resetPassword(token: string, newPassword: string) {
    return this.http.post<any>(`${this.authApiUrl}/reset-password`, { token, newPassword });
  }
}
